import app from 'flarum/forum/app';
import Page from 'flarum/components/Page';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Placeholder from 'flarum/common/components/Placeholder';
import ActivityPanel from '../components/ActivityPanel';
import LeaderboardPanel from '../components/LeaderboardPanel';
import StudentsPanel from '../components/StudentsPanel';
import WarehousePanel from '../components/WarehousePanel';
import SeedPickerModal from '../components/SeedPickerModal';
import { cropName } from '../../common/utils/cropNames.mjs';

import {
  expandFarm,
  loadMyFarm,
  loadStudentFarm,
  runPlotAction,
  runVisitAction,
  sellInventory,
} from '../utils/farmApi.mjs';
import { applyFarmResponse, createFarmState, mapFarmError, remainingMs, serverNow } from '../utils/farmState.mjs';
import { activeCareProblems, careEffectMessage } from '../utils/farmCare.mjs';
import { shouldRefreshAfterActionError } from '../utils/farmAction.mjs';
import { ownerHarvestQuantity } from '../utils/farmLabels.mjs';

const CARE_BUTTONS = {
  water: { icon: 'fas fa-tint', label: '浇水' },
  weeds: { icon: 'fas fa-leaf', label: '除草' },
  pests: { icon: 'fas fa-bug', label: '除虫' },
};

const formatDuration = (milliseconds) => {
  const seconds = Math.ceil(Math.max(0, milliseconds) / 1000);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = seconds % 60;
  if (hours > 0) return `${hours}小时${String(minutes).padStart(2, '0')}分`;
  return `${minutes}分${String(rest).padStart(2, '0')}秒`;
};

const responseData = (response) => response?.data || response;
const apiBase = () => app.forum.attribute('apiUrl') || '/api';

export default class FarmPage extends Page {
  oninit(vnode) {
    super.oninit(vnode);
    app.history.push('yingtaoFarm');
    this.bodyClass = 'App--yingtaoFarm';
    this.viewer = app.session.user;
    this.state = createFarmState();
    this.tick = 0;
    this.focusHandler = () => this.refreshCurrentView();
    window.addEventListener('focus', this.focusHandler);
    this.load();
  }

  oncreate(vnode) {
    super.oncreate(vnode);
    this.tick = window.setInterval(() => m.redraw(), 1000);
  }

  onremove(vnode) {
    super.onremove(vnode);
    window.removeEventListener('focus', this.focusHandler);
    if (this.tick) window.clearInterval(this.tick);
  }

  load() {
    this.state = { ...this.state, loading: true, error: '' };
    return loadMyFarm(app, apiBase())
      .then((response) => {
        this.state = applyFarmResponse(this.state, responseData(response), Date.now());
      })
      .catch((error) => {
        this.state = { ...this.state, loading: false, error: mapFarmError(error) };
      })
      .finally(() => m.redraw());
  }

  refreshCurrentView() {
    if (this.state.view === 'student' && this.state.selectedStudent) {
      return this.loadStudent(this.state.selectedStudent);
    }

    return this.load();
  }

  async action(key, operation) {
    if (this.state.busyKey) return;
    this.state = { ...this.state, busyKey: key, error: '', careNotice: '' };
    m.redraw();
    try {
      const response = await operation();
      const effect = responseData(response)?.effect;
      await this.refreshCurrentView();
      if (effect) this.state = { ...this.state, careNotice: careEffectMessage(effect) };
    } catch (error) {
      const message = mapFarmError(error);
      if (shouldRefreshAfterActionError(error)) {
        await this.refreshCurrentView();
      }
      this.state = { ...this.state, busyKey: null, error: message };
      m.redraw();
    }
  }

  plotAction(plot, action, payload = {}) {
    return this.action(`plot-${plot.plotNumber}`, () => runPlotAction(app, plot.plotNumber, action, payload, apiBase()));
  }

  view() {
    return (
      <div className="YingtaoFarmPage">
        <div className="container">
          <header className="YingtaoFarmHeader">
            <div>
              <p className="YingtaoFarm-eyebrow">Cherrymaker club · 樱桃农场</p>
              <h1>{this.state.farm?.user?.nickname || '我的农场'}</h1>
              {this.state.farm ? this.stats(this.state.farm.stats) : null}
            </div>
            <Button icon="fas fa-sync-alt" className="Button Button--link" disabled={this.state.loading} onclick={() => this.refreshCurrentView()}>
              刷新
            </Button>
          </header>
          <nav className="YingtaoFarmTabs" aria-label="农场视图">
            {[
              ['mine', '我的农场'],
              ['students', '同学农场'],
              ['leaderboard', '排行榜'],
              ['activity', '农场动态'],
            ].map(([key, label]) => (
              <button type="button" className={`YingtaoFarmTab${this.state.view === key ? ' is-active' : ''}`} onclick={() => {
                this.state.view = key;
                if (key === 'mine') {
                  this.load();
                  return;
                }
                m.redraw();
              }}>
                {label}
              </button>
            ))}
          </nav>
          {this.state.error ? <div className="Alert Alert--error" role="alert">{this.state.error}</div> : null}
          {this.state.careNotice ? <div className="Alert Alert--success" role="status">{this.state.careNotice}</div> : null}
          {this.state.loading && !this.state.farm ? <LoadingIndicator /> : this.content()}
        </div>
      </div>
    );
  }

  stats(stats) {
    return (
      <div className="YingtaoFarmStats">
        <span>等级 {stats.level}</span>
        <span>经验 {stats.experience}</span>
        <strong>金币 {stats.coins}</strong>
        <span>土地 {stats.unlockedPlots ?? '-'}/12</span>
      </div>
    );
  }

  content() {
    switch (this.state.view) {
      case 'students': return this.studentsView();
      case 'student': return this.studentView();
      case 'leaderboard': return this.leaderboardView();
      case 'activity': return this.activityView();
      default: return this.mineView();
    }
  }

  mineView() {
    const farm = this.state.farm;
    if (!farm) return <Placeholder text="暂时无法加载农场。" />;
    const nextPlot = Number(farm.stats.unlockedPlots || 0) + 1;
    const nextPrice = Number(farm.expansionPrices?.[nextPlot] || 0);
    const shortage = Math.max(0, nextPrice - Number(farm.stats.coins || 0));
    const fullyUnlocked = nextPlot > 12 || !nextPrice;
    return (
      <div className="YingtaoFarmLayout">
        <main className="YingtaoFarmMain">
          <div className="YingtaoFarmBoard" aria-label="我的土地">
            {(farm.plots || []).map((plot) => this.plotTile(plot, false))}
          </div>
          <div className="YingtaoFarmActions YingtaoFarmExpansion">
            <div>
              <strong>{fullyUnlocked ? '土地已全部解锁' : `下一块是第 ${nextPlot} 块土地`}</strong>
              {!fullyUnlocked ? <span>需要 {nextPrice} 金币{shortage > 0 ? `，还差 ${shortage} 金币` : '，金币已足够'}</span> : null}
            </div>
            <Button icon="fas fa-expand-arrows-alt" className="Button" disabled={this.state.busyKey !== null || fullyUnlocked || shortage > 0} onclick={() => this.action('expand', () => expandFarm(app, apiBase()))}>
              扩建下一块土地
            </Button>
          </div>
        </main>
        <WarehousePanel farm={farm} busy={this.state.busyKey !== null} onSell={(item) => this.action(`sell-${item.cropKey}`, () => sellInventory(app, item.cropKey, item.quantity, apiBase()))} />
      </div>
    );
  }

  plotTile(plot, visit) {
    const cycle = plot.cycle;
    const now = serverNow(this.state);
    const mature = cycle && remainingMs(cycle.maturesAt, now) === 0;
    const careProblems = cycle ? activeCareProblems(cycle, now) : [];
    const busy = this.state.busyKey === `plot-${plot.plotNumber}`;
    const action = (name) => visit
      ? this.action(`visit-${plot.plotNumber}`, () => runVisitAction(app, this.state.selectedStudent, plot.plotNumber, name, {}, apiBase()))
      : this.plotAction(plot, name);

    return (
      <article className={`YingtaoPlotTile${plot.isUnlocked ? '' : ' is-locked'}`} key={plot.plotNumber}>
        <div className="YingtaoPlotTile-number">土地 {plot.plotNumber}</div>
        {!plot.isUnlocked ? (
          <div className="YingtaoPlotTile-state">
            <i className="fas fa-lock" />
            {this.state.farm?.expansionPrices?.[plot.plotNumber]
              ? <span>需要 {this.state.farm.expansionPrices[plot.plotNumber]} 金币</span>
              : <span>土地尚未解锁</span>}
          </div>
        ) : null}
        {plot.isUnlocked && !cycle ? (
          <div className="YingtaoPlotTile-state">
            <i className="fas fa-seedling" /> 空地
            <Button className="Button Button--primary" disabled={busy} onclick={() => this.seedPicker(plot)}>选择种子</Button>
          </div>
        ) : null}
        {plot.isUnlocked && cycle ? (
          <div className="YingtaoPlotTile-state">
            <strong>{cropName(cycle.cropKey)}</strong>
            <span>{mature ? '已成熟' : `还需 ${formatDuration(remainingMs(cycle.maturesAt, now))}`}</span>
            <span className="YingtaoPlotTile-yield">{mature ? '当前可收获' : '预计收获'} {ownerHarvestQuantity(cycle)} 个</span>
            <div className="YingtaoPlotTile-actions">
              {!mature ? careProblems.map((type) => (
                <Button
                  className="Button Button--link YingtaoPlotCareButton"
                  icon={CARE_BUTTONS[type].icon}
                  title={CARE_BUTTONS[type].label}
                  aria-label={CARE_BUTTONS[type].label}
                  disabled={busy}
                  onclick={() => action(type)}
                >
                  {CARE_BUTTONS[type].label}
                </Button>
              )) : null}
              {mature ? <Button className="Button Button--primary" disabled={busy} onclick={() => action(visit ? 'steal' : 'harvest')}>{visit ? '偷菜' : '收获'}</Button> : null}
            </div>
          </div>
        ) : null}
      </article>
    );
  }

  seedPicker(plot) {
    app.modal.show(SeedPickerModal, {
      catalog: this.state.farm?.catalog || {},
      onChoose: (cropKey) => this.plotAction(plot, 'plant', { cropKey }),
    });
  }

  studentsView() {
    return <StudentsPanel app={app} basePath={apiBase()} onSelect={(id) => { this.state.selectedStudent = id; this.state.view = 'student'; this.loadStudent(id); }} />;
  }

  async loadStudent(id) {
    this.state = { ...this.state, loading: true };
    try {
      const response = await loadStudentFarm(app, id, apiBase());
      const farm = responseData(response);
      const serverTime = Date.parse(farm?.serverTime || '');
      this.state = {
        ...this.state,
        farm,
        loading: false,
        busyKey: null,
        view: 'student',
        serverOffsetMs: Number.isFinite(serverTime) ? serverTime - Date.now() : 0,
      };
    } catch (error) {
      this.state = { ...this.state, loading: false, busyKey: null, error: mapFarmError(error) };
    }
    m.redraw();
  }

  leaderboardView() {
    return <LeaderboardPanel app={app} basePath={apiBase()} />;
  }

  activityView() {
    return <ActivityPanel app={app} basePath={apiBase()} />;
  }

  studentView() {
    const farm = this.state.farm;
    if (!farm) return <Placeholder text="暂时无法加载同学农场。" />;
    return (
      <section className="YingtaoFarmPanel">
        <header className="YingtaoFarmPanel-header"><h2>{farm.user?.nickname || '同学农场'}</h2><Button className="Button Button--link" onclick={() => { this.state.view = 'students'; m.redraw(); }}>返回同学列表</Button></header>
        <div className="YingtaoFarmBoard YingtaoFarmBoard--student">{(farm.plots || []).map((plot) => this.plotTile(plot, true))}</div>
      </section>
    );
  }
}
